/**
 * The currently selected site, shared across every module that evaluates one.
 *
 * Compliance, Findings, Site detail and the Digital Twin all answer "for which site?",
 * and before this existed the answer was an archetype switcher that evaluated a TEMPLATE
 * rather than a real site — useful for exercising the engine, misleading as a product,
 * because the score shown belonged to no site anyone owns.
 *
 * The selection is persisted to localStorage. An operator who reloads mid-investigation
 * should not be dropped back to whichever site sorts first; and it is per-browser
 * convenience only, so a stale or absent value must degrade to a sensible default rather
 * than to an error.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHICH SITE A FRESH BROWSER LANDS ON, AND WHY IT IS NOT `sites[0]`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It was `sites[0]`, and `/sites` orders by name in byte order, so with the test fixtures
 * present a fresh browser landed on "Audit Coverage Site" — a fixture with no devices — and
 * with only the real fleet it landed on JAZZMSC5, because an uppercase J sorts before a
 * lowercase m. Neither is a choice anyone made; both looked like one. Worse, the default was
 * never written back, so it was recomputed on every load and could change under an operator
 * as sites were added.
 *
 * Now (Hamza, 2026-09-09): the default is the MOST RECENTLY REPORTING REAL SITE — the site
 * whose telemetry health shows the latest `lastSeenAt` — and sites that have never reported
 * are not offered by the header selector at all. That reads the fleet's own state instead of
 * an accident of collation, needs no configured value, and is written back once chosen so it
 * is stable across reloads. A site that has never reported is still a site: it stays on the
 * Sites page, and the onboarding flow selects a new site explicitly the moment it is created.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSiteHealth } from '@/lib/api/queries';
import { useSites, type SiteListItem } from '@/lib/api/sites';
import { SiteContext, type SiteContextValue } from './site-context-value';

const STORAGE_KEY = 'lumen.selectedSiteId';

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private windows and blocked site data throw on access, not just return null.
    return null;
  }
}

export function SiteProvider({ children }: { children: ReactNode }): ReactNode {
  const query = useSites();
  /*
   * Fleet health, for the default-site rule and the selectable list. The same cache entry
   * Overview's fleet view and the Alarms board's fleet mode read, so this costs no request
   * of its own once any of them has mounted.
   */
  const health = useSiteHealth();
  const sites = query.data?.data ?? EMPTY;
  const [selectedId, setSelectedId] = useState<string | null>(readStored);

  const select = useCallback((id: string) => {
    setSelectedId(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* a lost preference is not worth failing a click over */
    }
  }, []);

  /*
   * FORGETTING IS AN ACT TOO, and it needed its own function.
   *
   * `OrgProvider.select` already deletes this module's storage key when the organisation
   * changes — it says so, and names the key for exactly that reason. But the key is only the
   * SEED for `useState` and is read once at mount, so deleting it moved nothing: the live state
   * kept naming the site of the organisation we had just left. Clearing has to happen here,
   * where the state is.
   */
  const forget = useCallback(() => {
    setSelectedId(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* as above */
    }
  }, []);

  /** siteId -> health row, for "has this site ever reported, and when last". */
  const healthById = useMemo(
    () => new Map((health.data?.data ?? []).map((h) => [h.siteId, h])),
    [health.data],
  );

  /*
   * Reconcile against what actually exists, and pick a default where nothing valid is stored.
   *
   * A persisted id can name a site that was deleted, or one belonging to a tenant the user no
   * longer has access to — in both cases every downstream query 404s and the UI looks broken
   * rather than empty. When there is nothing valid, wait for health unless it has FAILED:
   * choosing before health arrives would be choosing by collation again, for one render, and
   * then switching under the operator.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * AN ORGANISATION THAT OWNS NO SITES USED TO KEEP THE LAST ONE'S
   * ───────────────────────────────────────────────────────────────────────────
   *
   * This opened `if (sites.length === 0) return;`, which reads as "nothing to reconcile
   * against" and is true of two completely different states: the list has not arrived yet, and
   * the list arrived empty. The second is not nothing — it is the answer.
   *
   * Measured: selecting a sub-organisation that owns zero sites left the header, the site hub,
   * the findings badge and the alarm badge all still pointed at `Jamshoro`, a site of the
   * organisation we had just left. `App.tsx` passes this `selectedId` to seven places, so one
   * stale id is seven screens making a claim about a tenant the data does not come from.
   * ADR-0001 makes a foreign site a 404, so the requests were refused — which is the safe
   * direction and also why nothing on screen said anything at all.
   *
   * The two states are now told apart by the QUERY, not by the length: loading and failed both
   * hold the selection, because destroying an operator's context over a transient error is
   * worse than a stale name for one render. Empty-and-settled clears it.
   */
  useEffect(() => {
    if (query.isLoading || query.isError) return;
    const stillThere = selectedId !== null && sites.some((s) => s.id === selectedId);
    if (stillThere) return;
    if (sites.length === 0) {
      /* Nothing to fall back to, so the honest answer is "no site selected" — which every
         consumer already renders, because it is the state a fresh browser starts in. */
      if (selectedId !== null) forget();
      return;
    }
    if (health.isLoading) return;

    const lastSeen = (s: SiteListItem): number => {
      const at = healthById.get(s.id)?.lastSeenAt;
      return at ? Date.parse(at) : 0;
    };
    const reported = sites.filter(
      (s) => (healthById.get(s.id)?.reportingState ?? 'never') !== 'never',
    );
    const pick = [...reported].sort((a, b) => lastSeen(b) - lastSeen(a))[0] ?? sites[0];
    if (pick) select(pick.id);
  }, [
    sites,
    selectedId,
    query.isLoading,
    query.isError,
    health.isLoading,
    healthById,
    select,
    forget,
  ]);

  /*
   * The selector's list: sites that have published at least once, plus the selected site
   * whatever its state — so a site chosen on the Sites page or just created by onboarding is
   * never a selection the header cannot display. While health is loading, everything is
   * offered rather than nothing: an empty dropdown for the duration of a request reads as a
   * fleet with no sites.
   */
  const selectable = useMemo(() => {
    if (health.isLoading || health.isError) return sites;
    return sites.filter(
      (s) => s.id === selectedId || (healthById.get(s.id)?.reportingState ?? 'never') !== 'never',
    );
  }, [sites, selectedId, health.isLoading, health.isError, healthById]);

  /*
   * WHAT IS PUBLISHED IS THE VALIDATED ID, NOT THE RAW STATE — belt to the effect's braces.
   *
   * The effect above repairs the selection, but it runs AFTER the render that discovered the
   * problem. One render with a foreign id is enough to fire seven requests from `App.tsx` for a
   * site this organisation does not own, and on a slow list it is more than one render. Deriving
   * what consumers see from the list they are being shown closes that window without the effect
   * having to win a race.
   *
   * HELD, NOT CLEARED, WHILE THE LIST IS UNKNOWN. `sites` is empty during the fetch and after a
   * failure, and validating against an empty list would blank the header on every reload — the
   * same mistake as treating `deviceIds: []` as "narrow to nothing". Only a settled, non-empty
   * list can say an id is foreign.
   */
  const publishedId = useMemo(() => {
    if (query.isLoading || query.isError) return selectedId;
    return selectedId !== null && sites.some((s) => s.id === selectedId) ? selectedId : null;
  }, [sites, selectedId, query.isLoading, query.isError]);

  const value = useMemo<SiteContextValue>(
    () => ({
      sites,
      selectable,
      loading: query.isLoading,
      error: query.isError,
      selectedId: publishedId,
      selected: sites.find((s) => s.id === publishedId) ?? null,
      select,
    }),
    [sites, selectable, query.isLoading, query.isError, publishedId, select],
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

/** Stable reference, so memos keyed on the list actually hold between renders. */
const EMPTY: SiteListItem[] = [];
